/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './FacilityAlertBanner.scss';

import * as React from 'react';
import { useLocation } from 'react-router';
import cx from 'classnames';

import {
    DisplayHtmlContent,
    Alert,
    ContentBlock,
    TextBlock,
    Button,
    DividerNew,
    Badge,
} from 'sarsaparilla';

interface FacilityAlertBannerProps extends React.HTMLAttributes<HTMLDivElement> {
    alerts?: Array<{ id: string; body: string }>;
}

export function FacilityAlertBanner({
    alerts,
    className,
    ...rest
}: FacilityAlertBannerProps) {
    const location = useLocation();
    const clampedDivRef = React.useRef<HTMLDivElement>(null);
    const [canBeClamped, setCanBeClamped] = React.useState(false);
    const [isClamped, setClamped] = React.useState(false);
    const [isExpanded, setExpanded] = React.useState(false);
    const validAlerts = React.useMemo(
        () => alerts?.filter((alert) => alert?.body?.trim()) ?? [],
        [alerts]
    );

    /**
     * If there's only 1 alert and it's not been clamped, we want to hide the toggle button.
     * To accomplish this we need to do some math on the clamped div. but this can not be
     * done in the main function body because you end up with the measurements of the
     * previous state.
     *
     * alerts.length is in the dependency array because alerts itself is probably not
     * memoized, but we need to account for alerts coming in after several initial renders.
     * Basically we're trading off watching the alert content closely for performance because
     * alerts are expected to not change often
     */
    React.useLayoutEffect(() => {
        const clampedDiv = clampedDivRef.current;

        if (!clampedDiv) {
            return () => {};
        }

        if (!isExpanded) {
            setCanBeClamped(clampedDiv?.scrollHeight > clampedDiv?.clientHeight);
        }

        setClamped(clampedDiv?.scrollHeight > clampedDiv?.clientHeight);

        // setup a resize observer because maybe the alert fits on one line at wider widths
        // but is truncated at smaller widths
        const observer = new ResizeObserver(() => {
            if (!isExpanded) {
                setCanBeClamped(clampedDiv?.scrollHeight > clampedDiv?.clientHeight);
            }

            setClamped(clampedDiv?.scrollHeight > clampedDiv?.clientHeight);
        });

        observer.observe(clampedDiv);

        return () => {
            observer.disconnect();
        };
    }, [isExpanded, validAlerts.length]);

    /**
     * Content hidden by overflow is still accessible to screen readers and keyboard focus.
     * So if the focus goes inside the hidden part, we auto-expand the alerts.
     * When focus leaves we reset the scrollTop because sometimes browsers will force
     * the scroll position to make the focused content visible even though it doesn't fit.
     */
    React.useEffect(() => {
        const clampedDiv = clampedDivRef.current;

        if (!clampedDiv) {
            return () => {};
        }

        const focusout = () => {
            clampedDiv.scrollTop = 0;
        };

        const focusin = () => {
            clampedDiv.scrollTop = 0;
            const activeElement = document.activeElement as HTMLElement;
            const lineHeight = 24;

            if (!isExpanded && activeElement.offsetTop > lineHeight) {
                setExpanded(true);
            }
        };

        clampedDiv.addEventListener('focusin', focusin);
        clampedDiv.addEventListener('focusout', focusout);

        return () => {
            clampedDiv?.removeEventListener('focusin', focusin);
            clampedDiv?.removeEventListener('focusout', focusout);
        };
    }, [isExpanded]);

    /**
     * Bail on rendering if there are no alerts
     */
    if (!validAlerts?.[0]?.body) {
        return null;
    }

    const [first, ...otherAlerts] = validAlerts;
    const shouldShowToggleButton = validAlerts.length > 1 || isClamped || canBeClamped;

    return (
        <div
            className={cx('shared--facility-alert-banner', className, {
                'has-toggle-button': shouldShowToggleButton,
            })}
            {...rest}
        >
            <ContentBlock>
                <div className="content">
                    <TextBlock
                        width="none"
                        className="content-alerts"
                        id="facility-alert-banner-alerts"
                    >
                        <Alert hasBoxStyling={false}>
                            <div
                                ref={clampedDivRef}
                                className={isExpanded ? undefined : 'is-collapsed'}
                            >
                                <DisplayHtmlContent html={`<div>${first.body}</div>`} />
                            </div>
                        </Alert>

                        {isExpanded
                            ? otherAlerts.map((alert) => {
                                  return (
                                      <React.Fragment key={alert.id}>
                                          <DividerNew color="yellow" className="my-2" />

                                          <Alert hasBoxStyling={false} key={alert.id}>
                                              <DisplayHtmlContent
                                                  key={alert.id}
                                                  html={alert.body}
                                              />
                                          </Alert>
                                      </React.Fragment>
                                  );
                              })
                            : null}
                    </TextBlock>

                    <div className="toggle-button-wrap">
                        {shouldShowToggleButton ? (
                            <Button
                                isUnstyled
                                aria-expanded={isExpanded}
                                aria-controls="facility-alert-banner-alerts"
                                className="toggle-button"
                                onClick={() => setExpanded((prev) => !prev)}
                                clickTagCategory="Facility Alert Banner Toggle"
                                clickTagAction={
                                    isExpanded ? 'Close Expanded Alert' : 'Expand Alert'
                                }
                                clickTagLabel={`Facility Alert Banner on ${location.pathname}`}
                                aria-label={
                                    isExpanded
                                        ? 'Facility Alerts: Show Less'
                                        : 'Facility Alerts: Show More'
                                }
                            >
                                <span className="inner">
                                    <Badge className="alert-badge-total-items">
                                        {validAlerts.length}
                                    </Badge>
                                    <span>{isExpanded ? 'Show Less' : 'Show More'}</span>
                                </span>
                            </Button>
                        ) : null}
                    </div>
                </div>
            </ContentBlock>
        </div>
    );
}
